import { ScanOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import gs1Parser from "gs1-parser"
import { useDisclosure } from "hooks/useDisclosure"
import { useElement } from "hooks/useElement"
import { useCallback } from "react"
import {
	BarcodeFormat,
	BarcodeScanner,
	DetectedBarcode,
	useStreamState,
} from "react-barcode-scanner"
import "react-barcode-scanner/polyfill"
import { createPortal } from "react-dom"
import { getOS } from "utils/os"

export function SerialNumberScanner({
	loading,
	onCapture,
}: {
	loading?: boolean
	onCapture: (serialNumber: string) => void
}) {
	const { isIOS } = getOS()
	const containerElement = useElement("layout-content")
	const modal = useDisclosure()
	const { notify } = useNotification()
	const [stream] = useStreamState()
	const handleCapture = useCallback(
		(barcodes: DetectedBarcode[]) => {
			if (barcodes.length === 0) {
				return
			}

			const [barcode] = barcodes

			// The leading \u001d usually signifies that the scanner is reporting the FNC1 (Function 1 Symbol Character).
			// It is effectively a flag saying "This is a GS1 code." Most commercial scanners strip this by default,
			// but since react-barcode-scanner is keeping it, the code above handles it cleanly.
			const cleanValue = barcode.rawValue.startsWith("\u001d")
				? barcode.rawValue.substring(1)
				: barcode.rawValue

			let serialNumber
			try {
				const decodedBarcode = gs1Parser.decode(cleanValue)
				serialNumber = decodedBarcode.serial
			} catch (error) {
				console.error(
					"Unknown barcode format",
					JSON.stringify(barcode),
					{ cleanValue },
					JSON.stringify(error),
				)
				serialNumber = barcode.rawValue
				notify.warning({
					message: (
						<p>
							The barcode was scanned, but is not in the expected format.
							<br />
							The possible serial number is "<b>{serialNumber}</b>"
						</p>
					),
				})
			} finally {
				onCapture(serialNumber ?? "")
				modal.close()
			}
		},
		[modal, notify, onCapture],
	)

	return (
		<>
			<Button
				icon={<ScanOutlined aria-hidden />}
				loading={modal.isOpen || loading}
				aria-label="Open serial number scanner"
				disabled={isIOS}
				onClick={modal.open}
			/>

			{modal.isOpen &&
				containerElement &&
				createPortal(
					<div
						role="dialog"
						aria-label="Serial number scanner"
						className="absolute inset-0 z-10 flex flex-col gap-2 bg-white p-5"
					>
						<div className="relative flex min-h-0 grow">
							{!stream?.active && (
								<Spinner className="absolute top-1/2 left-1/2" />
							)}

							<BarcodeScanner
								aria-label="Place the barcode within the camera frame"
								className="h-full w-full rounded-lg object-contain"
								options={{ formats: [BarcodeFormat.CODE_128, "data_matrix"] }}
								onCapture={handleCapture}
							/>
						</div>
						<div className="w-full">
							<Button
								size="large"
								className="w-full rounded-s-lg"
								onClick={modal.close}
							>
								Close
							</Button>
						</div>
					</div>,
					containerElement,
				)}
		</>
	)
}
